import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Card, CardContent, Avatar, 
  LinearProgress, Alert, Chip, Grid, Paper, Divider
} from '@mui/material';
import {
  Person as PersonIcon, School as SchoolIcon, 
  Security as SecurityIcon, CheckCircle as CheckCircleIcon,
  Error as ErrorIcon, LocationOn as LocationIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CommonAppBar from '../components/CommonAppBar';
import Footer from './Footer';

// Theme matching your app's design system
const modernTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(18,82,166)',
      light: 'rgb(59,130,246)',
      dark: 'rgb(14,65,130)',
    },
    secondary: {
      main: 'rgb(18,82,166)',
      light: 'rgb(59,130,246)',
      dark: 'rgb(14,65,130)',
    },
    success: {
      main: 'rgb(34, 197, 94)',
      light: 'rgb(74, 222, 128)',
      dark: 'rgb(22, 163, 74)',
    },
    warning: {
      main: 'rgb(245, 158, 11)',
      light: 'rgb(251, 191, 36)',
      dark: 'rgb(217, 119, 6)',
    },
    error: {
      main: 'rgb(239, 68, 68)',
      light: 'rgb(248, 113, 113)',
      dark: 'rgb(220, 38, 38)',
    },
  },
});

const Verify = () => {
  const { user_tok, userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState(null);
  const [error, setError] = useState(null);

  // Get the token from either parameter name
  const token = user_tok || userId;

  useEffect(() => {
    if (token) {
      verifyCandidate();
    } else {
      setError('No verification token provided');
      setLoading(false);
    }
  }, [token]);

  const verifyCandidate = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`https://ngat.ethernet.edu.et/api/verify?user_tok=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle the response structure
      if (data.success && data.data) {
        setCandidateData(data.data);
      } else {
        setCandidateData(data);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Failed to verify candidate. Please check the QR code or try again.');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <ThemeProvider theme={modernTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
          <CommonAppBar />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="h6" color="primary">
                Verifying Candidate Information...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we verify the candidate details.
              </Typography>
            </Card>
          </Container>
          <Footer />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={modernTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
          <CommonAppBar />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Card sx={{ p: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Verification Failed
                </Typography>
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Token: {user_tok}
                </Typography>
              </Box>
            </Card>
          </Container>
          <Footer />
        </Box>
      </ThemeProvider>
    );
  }

  if (!candidateData) {
    return (
      <ThemeProvider theme={modernTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
          <CommonAppBar />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Card sx={{ p: 3 }}>
              <Alert severity="warning">
                <Typography variant="h6" gutterBottom>
                  No Data Found
                </Typography>
                <Typography variant="body2">
                  No candidate information found for the provided token.
                </Typography>
              </Alert>
            </Card>
          </Container>
          <Footer />
        </Box>
      </ThemeProvider>
    );
  }

  const fullName = candidateData.fullName || `${candidateData.firstName || ''} ${candidateData.fatherName || ''} ${candidateData.grandFatherName || ''}`.trim();

  return (
    <ThemeProvider theme={modernTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <CommonAppBar />
        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Verification Success Header */}
          <Card sx={{ mb: 3, p: 3, textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
            <CheckCircleIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Candidate Verified
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {fullName || 'Candidate Name'}
            </Typography>
          </Card>

          <Grid container spacing={3}>
            {/* Candidate Photo and Basic Info */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center' }}>
                <CardContent>
                  <Avatar
                    src={candidateData.photo || null}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mx: 'auto', 
                      mb: 2,
                      fontSize: '2rem',
                      bgcolor: 'primary.main'
                    }}
                  >
                    {!candidateData.photo && fullName ? fullName.charAt(0).toUpperCase() : 'C'}
                  </Avatar>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {fullName || 'Candidate Name'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {candidateData.gender || 'Not specified'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Exam Credentials */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon color="primary" />
                    Exam Credentials
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Username</Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ 
                          fontFamily: 'monospace', 
                          bgcolor: 'grey.100', 
                          p: 1, 
                          borderRadius: 1 
                        }}>
                          {candidateData.username || 'Not assigned'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Exam Center</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {candidateData.examcenter || candidateData.exam_center_name || 'Not assigned'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Session</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {candidateData.session || candidateData.Session || 'Not assigned'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Location</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {candidateData.location || 'Not specified'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Verification Footer */}
          <Card sx={{ mt: 3, p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              Verified on {new Date().toLocaleString()}
            </Typography>
          </Card>
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default Verify;
